import {
  DataVersion,
  IncidentNeighbourAlarm,
  IncidentProduct,
} from '@hakimo-ui/hakimo/types';
import { useEffect, useRef } from 'react';
import { Panel } from '../Panel';
import {
  alarmDetailPath,
  alarmStartTimeValue,
  formatAlarmStartTime,
  shortAlarmId,
} from '../util';

interface Props {
  alarms: IncidentNeighbourAlarm[];
  current: IncidentNeighbourAlarm;
  product: IncidentProduct;
  loading: boolean;
  dataVersion?: DataVersion;
}

function StatusPill({
  escalated,
  isCurrent,
}: {
  escalated: boolean;
  isCurrent: boolean;
}) {
  const red = isCurrent || escalated;
  const styles = red
    ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  const label = isCurrent
    ? 'Current Incident'
    : escalated
    ? 'Escalated'
    : 'Not Escalated';
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${styles}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          red ? 'bg-red-500' : 'bg-green-500'
        }`}
      />
      {label}
    </span>
  );
}

function withCurrent(
  alarms: IncidentNeighbourAlarm[],
  current: IncidentNeighbourAlarm
): IncidentNeighbourAlarm[] {
  const others = alarms.filter(
    (alarm) => String(alarm.id) !== String(current.id)
  );
  return [current, ...others].sort(
    (a, b) => alarmStartTimeValue(b) - alarmStartTimeValue(a)
  );
}

export function NeighbouringAlarmsPanel({
  alarms,
  current,
  product,
  loading,
  dataVersion,
}: Props) {
  const rows = withCurrent(alarms, current);
  const listRef = useRef<HTMLUListElement>(null);
  const currentRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const list = listRef.current;
    const row = currentRef.current;
    if (!list || !row) {
      return;
    }
    const listRect = list.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    list.scrollTop +=
      rowRect.top - listRect.top - (list.clientHeight - rowRect.height) / 2;
  }, [rows.length, current.id]);

  return (
    <Panel
      title="Neighboring Alarms"
      action={
        <span className="text-onlight-text-2 dark:text-ondark-text-2 dark:bg-ondark-bg-3 bg-onlight-bg-2 rounded-full px-2 py-0.5 text-xs font-medium">
          {rows.length} {rows.length === 1 ? 'Alarm' : 'Alarms'}
        </span>
      }
    >
      {loading && alarms.length === 0 ? (
        <p className="text-onlight-text-3 dark:text-ondark-text-2 mt-3 text-sm">
          Loading…
        </p>
      ) : (
        <ul
          ref={listRef}
          className="mt-3 max-h-60 space-y-3 overflow-y-auto pr-2"
        >
          {rows.map((alarm) => {
            const isCurrent = String(alarm.id) === String(current.id);
            return (
              <li
                key={alarm.id}
                ref={isCurrent ? currentRef : undefined}
                className="flex items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <a
                    href={alarmDetailPath(product, alarm.id, dataVersion)}
                    target="_blank"
                    rel="noreferrer"
                    title={String(alarm.id)}
                    className={`block truncate font-medium underline ${
                      isCurrent
                        ? 'text-base text-red-600 hover:text-red-800 dark:text-red-400'
                        : 'text-primary-600 hover:text-primary-800 dark:text-primary-400 text-sm'
                    }`}
                  >
                    {shortAlarmId(alarm.id)}
                  </a>
                  <div className="text-onlight-text-3 dark:text-ondark-text-2 text-xs">
                    {formatAlarmStartTime(alarm)}
                  </div>
                </div>
                <StatusPill
                  escalated={alarm.is_escalated}
                  isCurrent={isCurrent}
                />
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

export default NeighbouringAlarmsPanel;
